import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";

import {
  PriceCategoryStatus,
  type PriceCategoryType,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useUpdatePriceCategoryMutation } from "PriceCategories/Api/Mutations/useUpdatePriceCategoryMutation";
import { useUpdatePriceCategoryStatusMutation } from "PriceCategories/Api/Mutations/useUpdatePriceCategoryStatusMutation";
import { usePriceCategoriesListQuery } from "PriceCategories/Api/Queries/usePriceCategoriesListQuery";
import { EditPriceCategoryModal } from "PriceCategories/Components/EditPriceCategoryModal";
import { PriceCategoryInformation } from "PriceCategories/Components/PriceCategoryInformation";
import type { PriceCategoryFormData } from "PriceCategories/Forms/priceCategoryFormSchema";
import { BackButton } from "Shared/Buttons/BackButton";
import { AuthenticatedLayout } from "Shared/Layouts/AuthenticatedLayout";

export const PriceCategoryDetailPage = (): React.ReactNode => {
  const { t } = useResource();
  const { priceCategoryId } = useParams<{ priceCategoryId: string }>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { data: categoriesData, isLoading } = usePriceCategoriesListQuery({
    offset: 0,
    limit: 1000,
  });

  const priceCategory = useMemo(
    () =>
      categoriesData?.items.find(
        (item) => item.priceCategoryID === priceCategoryId,
      ),
    [categoriesData, priceCategoryId],
  );

  const updateMutation = useUpdatePriceCategoryMutation();
  const updateStatusMutation = useUpdatePriceCategoryStatusMutation();

  const handleUpdate = async (formData: PriceCategoryFormData) => {
    if (!priceCategoryId) return;

    try {
      await updateMutation.mutateAsync({
        priceCategoryID: priceCategoryId,
        data: {
          name: formData.name,
          type: formData.type as PriceCategoryType,
          commissionRate: formData.commissionRate,
          defaultPrice: formData.defaultPrice,
        },
      });
      toast.success(t(Resources.PriceCategories.EditModal.SuccessMessage));
      setIsEditModalOpen(false);
    } catch {
      // error handled by hook
    }
  };

  const handleStatusChange = async () => {
    if (!priceCategoryId || !priceCategory) return;

    const newStatus =
      priceCategory.status === PriceCategoryStatus.Active
        ? PriceCategoryStatus.Inactive
        : PriceCategoryStatus.Active;

    try {
      await updateStatusMutation.mutateAsync({
        priceCategoryID: priceCategoryId,
        status: newStatus,
      });

      toast.success(
        newStatus === PriceCategoryStatus.Active
          ? t(Resources.PriceCategories.Detail.ActivateSuccess)
          : t(Resources.PriceCategories.Detail.DeactivateSuccess),
      );
    } catch {
      toast.error(t(Resources.PriceCategories.Detail.StatusChangeError));
    }
  };

  if (isLoading) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.PriceCategories)}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <CircularProgress />
        </Box>
      </AuthenticatedLayout>
    );
  }

  if (!priceCategory) {
    return (
      <AuthenticatedLayout title={t(Resources.Common.PriceCategories)}>
        <Typography>{t(Resources.PriceCategories.Detail.NotFound)}</Typography>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout
      preTitle={
        <BackButton
          defaultText={t(Resources.Common.BackToPriceCategories)}
          defaultPath={AppRouting.PriceCategories.path()}
        />
      }
      title={priceCategory.name}
      action={
        <Stack direction="row" spacing={Spacing.S}>
          <Button
            variant="contained"
            color={
              priceCategory.status === PriceCategoryStatus.Active
                ? "error"
                : "success"
            }
            onClick={handleStatusChange}
            disabled={updateStatusMutation.isPending}
          >
            {priceCategory.status === PriceCategoryStatus.Active
              ? t(Resources.PriceCategories.Detail.Deactivate)
              : t(Resources.PriceCategories.Detail.Activate)}
          </Button>
        </Stack>
      }
    >
      <Box sx={{ maxWidth: 600 }}>
        <PriceCategoryInformation
          name={priceCategory.name}
          type={priceCategory.type}
          commissionRate={priceCategory.commissionRate}
          defaultPrice={priceCategory.defaultPrice}
          dateCreated={priceCategory.dateCreated}
          dateUpdated={priceCategory.dateUpdated}
          onEdit={() => setIsEditModalOpen(true)}
        />
      </Box>
      <EditPriceCategoryModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onConfirm={handleUpdate}
        priceCategory={priceCategory}
        serverError={updateMutation.error}
      />
    </AuthenticatedLayout>
  );
};
